// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Banner policy for data-gap alarms.
 *
 * The global alerts bar is an *incident* surface: something is broken now.
 * Resolved (or ended) rows stay out of it even when they are still
 * unacknowledged — a recovered 60s blip must not paint the whole app orange
 * next to a healthy chart. Network Metrics still lists resolved gaps in-page.
 */

/** Warning/critical row that has not been closed. */
export function isOngoingDataGap(gap) {
  if (!gap || typeof gap !== 'object') return false;
  const sev = gap.severity;
  if (sev !== 'warning' && sev !== 'critical') return false;
  if (gap.gap_end) return false;
  return true;
}

/**
 * Deduplicate rows from parallel severity-filtered GETs.
 * @param {Array<unknown>} lists
 * @returns {object[]}
 */
export function mergeDataGapRows(lists) {
  const byId = new Map();
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const gap of list) {
      if (gap && gap.id != null) byId.set(gap.id, gap);
    }
  }
  return [...byId.values()];
}

/**
 * Group ongoing gaps into one banner row per stargate.
 * Message/locale formatting stays in AlertsContext.
 *
 * @param {unknown} gaps
 * @returns {Array<{
 *   alertId: string,
 *   stargateId: *,
 *   name: string,
 *   serial: string|null,
 *   ongoing: number,
 *   totalMissed: number,
 *   gapIds: number[],
 *   latestGapStart: Date|null,
 *   latestGapEnd: Date|null,
 *   latestMetricType: string|null,
 *   anyCritical: boolean
 * }>}
 */
export function buildDataGapBannerAlerts(gaps) {
  const byStargate = new Map();
  for (const gap of Array.isArray(gaps) ? gaps : []) {
    if (!isOngoingDataGap(gap)) continue;
    const sgId = gap.stargate_id;
    if (sgId == null) continue;
    if (!byStargate.has(sgId)) {
      byStargate.set(sgId, {
        name: gap.device_name || gap.device_serial || `Stargate #${sgId}`,
        serial: gap.device_serial || null,
        anyCritical: false,
        ongoing: 0,
        totalMissed: 0,
        gapIds: [],
        latestGapStart: null,
        latestGapEnd: null,
        latestMetricType: null,
      });
    }
    const entry = byStargate.get(sgId);
    entry.ongoing += 1;
    entry.gapIds.push(gap.id);
    entry.totalMissed += Number(gap.missed_points) || 0;
    if (gap.severity === 'critical') entry.anyCritical = true;
    if (gap.gap_start) {
      const ts = new Date(gap.gap_start);
      if (!Number.isNaN(ts.getTime()) && (!entry.latestGapStart || ts > entry.latestGapStart)) {
        entry.latestGapStart = ts;
        entry.latestGapEnd = null;
        entry.latestMetricType = gap.metric_type || null;
      }
    }
  }

  const rows = [];
  for (const [sgId, info] of byStargate.entries()) {
    info.gapIds.sort((a, b) => a - b);
    rows.push({
      alertId: `gap_sg_${sgId}_${info.gapIds[info.gapIds.length - 1]}`,
      stargateId: sgId,
      name: info.name,
      serial: info.serial,
      ongoing: info.ongoing,
      totalMissed: info.totalMissed,
      gapIds: info.gapIds,
      latestGapStart: info.latestGapStart,
      latestGapEnd: info.latestGapEnd,
      latestMetricType: info.latestMetricType,
      anyCritical: info.anyCritical,
    });
  }
  return rows;
}
